import type { StepInputRendererProps, StepItem } from "./StepInputRenderer";

import { cn, formatNumber } from "@homeinstead/ui";
import { RatingStars } from "@homeinstead/ui/components/Rating";
import { isDisplayableRating } from "~www/lib/office/get-aggregate-rating";
import {
  resolveContentAlignmentClass,
  resolveTextSizeClass,
} from "./form-item-styles";

type ReviewStarsStepItem = Extract<StepItem, { _type: "reviewStars" }>;

const DEFAULT_REVIEW_TEXT =
  "{{reviewStarCount}} star rating on {{reviewPlatform}} ({{roundedReviewCount}} reviews)";

const DEFAULT_ARIA_LABEL = "Rating: {{reviewStarCount}} out of 5";

function resolveReviewText(
  template: string,
  values: {
    reviewStarCount: number;
    reviewPlatform: string;
    roundedReviewCount: string;
  },
): string {
  return template
    .replaceAll("{{reviewStarCount}}", String(values.reviewStarCount))
    .replaceAll("{{reviewPlatform}}", values.reviewPlatform)
    .replaceAll("{{roundedReviewCount}}", values.roundedReviewCount);
}

export function ReviewStarsFormItem({
  input,
  officeRating,
  heroVariantStyles,
}: StepInputRendererProps) {
  if (input._type !== "reviewStars") {
    return null;
  }

  const {
    source,
    starVariant,
    reviewPlatform,
    reviewText,
    ariaLabel,
    contentAlignment,
    manualRating,
    manualReviewCount,
  } = input as ReviewStarsStepItem;

  const platform = reviewPlatform ?? "Google";
  const template = reviewText ?? DEFAULT_REVIEW_TEXT;

  let rating: number;
  let reviewCount: number;

  if (source === "manual") {
    if (!manualRating || !manualReviewCount) {
      return null;
    }
    rating = manualRating;
    reviewCount = manualReviewCount;
  } else {
    if (!isDisplayableRating(officeRating)) {
      return null;
    }
    rating = officeRating.rating;
    reviewCount = officeRating.ratingCount;
  }

  const placeholders = {
    reviewPlatform: platform,
    reviewStarCount: rating,
    roundedReviewCount: formatNumber(reviewCount),
  };

  const label = resolveReviewText(template, placeholders);
  const resolvedAriaLabel = resolveReviewText(
    ariaLabel ?? DEFAULT_ARIA_LABEL,
    placeholders,
  );

  const resolvedStarVariant = starVariant ?? "default";
  const starColor =
    resolvedStarVariant === "gold"
      ? "gold"
      : (heroVariantStyles?.ratingStarsColor ?? "default");

  return (
    <div
      className={cn(
        "flex w-fit flex-row items-center gap-3 font-bold",
        resolveContentAlignmentClass(contentAlignment),
      )}
    >
      <RatingStars
        ariaLabel={resolvedAriaLabel}
        color={starColor}
        rating={rating}
      />
      <span className={resolveTextSizeClass(input.textSize, "sm")}>
        {label}
      </span>
    </div>
  );
}
